import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PlayerStatsInterface } from '../../models/PlayerStats';
import { StatsContainer, PageTitle, StatsGrid, StatsHeader, ControlsSection, ControlsToggle, ControlsPanel, SearchSection, LoadingState, ErrorState, SortControls, SortButton, FilterControls, FilterButton } from './PlayerStatsStyles';
import { TigersPlayerCard } from '../../components/TigersPlayerCard';
import { PlayerInfo } from '../../models/PlayerInfo';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';
interface CombinedPlayerData extends PlayerStatsInterface {
  position: string;
  number: number;
}

type SortField = 'name' | 'number' | 'gamesPlayed' | 'goals' | 'assists' | 'points' | 'ppg' | 'pims' | 'wins' | 'losses' | 'so' | 'ga' | 'gaa';
type SortOrder = 'asc' | 'desc' | null;

interface SortConfig {
  field: SortField;
  order: SortOrder;
}

type Position = 'All' | 'Forward' | 'Defense' | 'Goalie';

const PlayerStats: React.FC = () => {
  const [playerData, setPlayerData] = useState<CombinedPlayerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'ppg', order: 'desc' });
  const [selectedPosition, setSelectedPosition] = useState<Position>('All');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isControlsOpen, setIsControlsOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

        const [skaterStatsItems, goalieStatsItems, playerItems] = await Promise.all([
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('tigers_stats'),
            CACHE_KEYS.skaterStats,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerStatsInterface>(
            entriesUrl('tigers_goalie_stats'),
            CACHE_KEYS.goalieStats,
            CACHE_TTL_MS
          ),
          fetchItemsWithCache<PlayerInfo>(
            entriesUrl('tigers_players'),
            CACHE_KEYS.players,
            CACHE_TTL_MS
          ),
        ]);

        if (!isMounted) return;

        const playerMap: Map<string, PlayerInfo> = new Map(
          (playerItems ?? []).map((player: PlayerInfo) => [player.name, player])
        );

        const isGoaliePosition = (position: string | undefined) =>
          (position ?? '').split('/').some((pos) => pos.trim() === 'Goalie');

        const combinedData: CombinedPlayerData[] = [];

        // Skaters come from `tigers_stats`
        for (const stat of skaterStatsItems ?? []) {
          const playerInfo = playerMap.get(stat.player);
          const goalie = isGoaliePosition(playerInfo?.position);
          if (goalie) continue;

          combinedData.push({
            ...stat,
            position: playerInfo?.position ?? 'Forward',
            number: playerInfo?.number ?? 0,
          });
        }

        // Goalies come from `tigers_goalie_stats`
        for (const stat of goalieStatsItems ?? []) {
          const playerInfo = playerMap.get(stat.player);
          const goalie = isGoaliePosition(playerInfo?.position);
          if (!goalie) continue;

          const gamesPlayed = Number(stat.gamesPlayed ?? 0) || 0;
          const ga = Number((stat as any).ga ?? 0) || 0;
          const gaa = gamesPlayed > 0 ? ga / gamesPlayed : 0;

          combinedData.push({
            player: stat.player,
            number: playerInfo?.number ?? 0,
            gamesPlayed,
            // Skater fields not present in goalie table
            goals: 0,
            assists: 0,
            points: 0,
            ppg: 0,
            pims: 0,
            position: playerInfo?.position ?? 'Goalie',
            wins: Number((stat as any).wins ?? 0) || 0,
            losses: Number((stat as any).losses ?? 0) || 0,
            so: Number((stat as any).so ?? 0) || 0,
            ga,
            gaa: Number((stat as any).gaa ?? gaa) || gaa,
          });
        }

        // Sort by PPG (descending) by default
        combinedData.sort((a: CombinedPlayerData, b: CombinedPlayerData) => b.ppg - a.ppg);

        if (isMounted) {
          setPlayerData(combinedData);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading player data:', err);
        if (isMounted) {
          setError('Unable to load player statistics. Please try again later.');
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);


  const handleSort = useCallback((field: SortField) => {
    setSortConfig(prevConfig => ({
      field,
      order: prevConfig.field === field 
        ? prevConfig.order === 'desc' 
          ? 'asc' 
          : prevConfig.order === 'asc' 
            ? null 
            : 'desc'
        : 'desc'
    }));
  }, []);

  const getSortIcon = useCallback((field: SortField) => {
    if (sortConfig.field !== field) return '○';
    if (sortConfig.order === 'desc') return '▼';
    if (sortConfig.order === 'asc') return '▲';
    return '○';
  }, [sortConfig]);

  const handlePositionFilter = useCallback((position: Position) => {
    setSelectedPosition(position);
  }, []);

  const matchesPosition = useCallback((playerPosition: string, filterPosition: Position): boolean => {
    if (filterPosition === 'All') return true;
    return playerPosition.split('/').some(pos => pos.trim() === filterPosition);
  }, []);

  const sortedAndFilteredPlayers = useMemo(() => {
    let result = playerData.filter(player => {
      const matchesSearch = player.player.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPositionFilter = matchesPosition(player.position, selectedPosition);
      
      // If goalie filter is NOT on, exclude goalies
      // If goalie filter IS on, only show goalies
      const isGoalie = player.position.split('/').some(pos => pos.trim() === 'Goalie');
      const shouldShowGoalie = selectedPosition === 'Goalie' ? isGoalie : !isGoalie;
      
      return matchesSearch && matchesPositionFilter && shouldShowGoalie;
    });

    if (sortConfig.order) {
      result = [...result].sort((a, b) => {
        if (sortConfig.field === 'name') {
          return sortConfig.order === 'asc'
            ? a.player.localeCompare(b.player)
            : b.player.localeCompare(a.player);
        }
        
        const aValue = a[sortConfig.field] ?? 0;
        const bValue = b[sortConfig.field] ?? 0;
        
        if (sortConfig.order === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    }

    return result;
  }, [playerData, searchTerm, sortConfig, selectedPosition, matchesPosition]);

  if (loading) {
    return (
      <StatsContainer>
        <LoadingState>Loading player statistics...</LoadingState>
      </StatsContainer>
    );
  }

  if (error) {
    return (
      <StatsContainer>
        <ErrorState>{error}</ErrorState>
      </StatsContainer>
    );
  }

  return (
    <StatsContainer>
      <PageTitle>Player Statistics</PageTitle>
      
      <ControlsSection isOpen={isControlsOpen}>
        <ControlsToggle 
          onClick={() => setIsControlsOpen(!isControlsOpen)}
          isOpen={isControlsOpen}
        >
          Filter & Sort
          <svg 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </ControlsToggle>

        <ControlsPanel isOpen={isControlsOpen}>
          <SearchSection>
            <input
              type="text"
              placeholder="Search players..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </SearchSection>

          <FilterControls isOpen={isControlsOpen}>
              <FilterButton 
                isActive={selectedPosition === 'All'}
                onClick={() => {
                  handlePositionFilter('All');
                }}
              >
                All Players
              </FilterButton>
              <FilterButton 
                isActive={selectedPosition === 'Forward'}
                onClick={() => {
                  handlePositionFilter('Forward');
                }}
              >
                Forwards
              </FilterButton>
              <FilterButton 
                isActive={selectedPosition === 'Defense'}
                onClick={() => {
                  handlePositionFilter('Defense');
                }}
              >
                Defense
              </FilterButton>
            <FilterButton 
              isActive={selectedPosition === 'Goalie'}
              onClick={() => {
                handlePositionFilter('Goalie');
              }}
            >
              Goalies
            </FilterButton>
          </FilterControls>

          <SortControls>
            <SortButton 
              isActive={sortConfig.field === 'name'} 
              onClick={() => handleSort('name')}
            >
              Name {getSortIcon('name')}
            </SortButton>
            <SortButton 
              isActive={sortConfig.field === 'number'} 
              onClick={() => handleSort('number')}
            >
              Number {getSortIcon('number')}
            </SortButton>
            <SortButton 
              isActive={sortConfig.field === 'gamesPlayed'} 
              onClick={() => handleSort('gamesPlayed')}
            >
              Games {getSortIcon('gamesPlayed')}
            </SortButton>
            {selectedPosition !== 'Goalie' && (
              <>
            <SortButton 
              isActive={sortConfig.field === 'goals'} 
              onClick={() => handleSort('goals')}
            >
              Goals {getSortIcon('goals')}
            </SortButton>
            <SortButton 
              isActive={sortConfig.field === 'assists'} 
              onClick={() => handleSort('assists')}
            >
              Assists {getSortIcon('assists')}
            </SortButton>
            <SortButton 
              isActive={sortConfig.field === 'points'} 
              onClick={() => handleSort('points')}
            >
              Points {getSortIcon('points')}
            </SortButton>
            <SortButton 
              isActive={sortConfig.field === 'ppg'} 
              onClick={() => handleSort('ppg')}
            >
              PPG {getSortIcon('ppg')}
            </SortButton>
            <SortButton 
              isActive={sortConfig.field === 'pims'} 
              onClick={() => handleSort('pims')}
            >
              PIMS {getSortIcon('pims')}
            </SortButton>
            </>
            )}
            {selectedPosition === 'Goalie' && (
              <>
                <SortButton 
                  isActive={sortConfig.field === 'wins'} 
                  onClick={() => handleSort('wins')}
                >
                  Wins {getSortIcon('wins')}
                </SortButton>
                <SortButton 
                  isActive={sortConfig.field === 'losses'} 
                  onClick={() => handleSort('losses')}
                >
                  Losses {getSortIcon('losses')}
                </SortButton>
                <SortButton 
                  isActive={sortConfig.field === 'so'} 
                  onClick={() => handleSort('so')}
                >
                  SO {getSortIcon('so')}
                </SortButton>
                <SortButton 
                  isActive={sortConfig.field === 'ga'} 
                  onClick={() => handleSort('ga')}
                >
                  GA {getSortIcon('ga')}
                </SortButton>
                <SortButton 
                  isActive={sortConfig.field === 'gaa'} 
                  onClick={() => handleSort('gaa')}
                >
                  GAA {getSortIcon('gaa')}
                </SortButton>
              </>
            )}
          </SortControls>
        </ControlsPanel>
      </ControlsSection>

      <StatsGrid>
        <StatsHeader isGoalieView={selectedPosition === 'Goalie'}>
          <div className="header-cell">Player</div>
          {selectedPosition === 'Goalie' ? (
            <>
              <div className="header-cell">GP</div>
              <div className="header-cell">W</div>
              <div className="header-cell">L</div>
              <div className="header-cell">SO</div>
              <div className="header-cell">GA</div>
              <div className="header-cell">GAA</div>
            </>
          ) : (
            <>
              <div className="header-cell">GP</div>
              <div className="header-cell">G</div>
              <div className="header-cell">A</div>
              <div className="header-cell">PTS</div>
              <div className="header-cell">PPG</div>
              <div className="header-cell">PIM</div>
            </>
          )}
        </StatsHeader>
        
        {sortedAndFilteredPlayers.length === 0 ? (
          <ErrorState>No players found matching your search criteria.</ErrorState>
        ) : (
          sortedAndFilteredPlayers.map(player => (
            <TigersPlayerCard 
              key={player.player} 
              player={player} 
              isMobile={isMobile}
              isGoalieView={selectedPosition === 'Goalie'}
            />
          ))
        )}
      </StatsGrid>
      
      <p style={{ textAlign: 'center', color: 'var(--gray)', marginTop: '2rem' }}>
        * Statistics are updated after each game
      </p>
    </StatsContainer>
  );
};

export default PlayerStats; 